<template>
  <div>
    <b-row>
      <b-col cols="12">
        <b-form-group
          :label="$t('modals.createPersonalizationSet.microsoft.chooseMarketingList') + '*'"
        >
          <b-form-select
            v-model="marketingList"
            :options="marketingListsOptions"
          ></b-form-select>
        </b-form-group>
        <b-alert
          v-if="autoSelectedList"
          show
          variant="info"
          class="mt-2"
        >
          <b-icon icon="info-circle-fill" class="mr-2"></b-icon>
          Auto-selected "{{ autoSelectedList }}" based on personalization set name. You can change this selection if needed.
        </b-alert>
        <p class="font-italic">
          {{ $t('modals.createPersonalizationSet.microsoft.chooseMarketingListDesc') }}
        </p>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="auto" class="mr-auto">
        <b-button
          size="lg"
          @click="$emit('prev-step')"
          class="green-button border-0 mt-2"
        >
          {{ $t('modals.createPersonalizationSet.back') }}
        </b-button>
      </b-col>
      <b-col cols="auto">
        <b-button
          size="lg"
          type="submit"
          class="green-button border-0 mt-2"
          :disabled="marketingList === null"
          @click="next"
        >
          {{ $t('modals.createPersonalizationSet.next') }}
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import GetMicrosoftCRMMarketingLists from '@/graphQlQueries/queries/getMicrosoftCRMMarketingLists';
import SetLoadingIndicator from '@/graphQlQueries/mutations/setLoadingIndicatorInCache';

export default {
  name: 'PersonalizationMicrosoftStepTwo',
  props: {
    marketingListIdProp: {
      type: String,
      required: true,
    },
    personalizationSetName: {
      type: String,
      default: '',
    },
    isReimport: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    marketingList: null,
    marketingListsOptions: [],
    autoSelectedList: null,
  }),
  apollo: {
    marketingListsOptions: {
      query: GetMicrosoftCRMMarketingLists,
      update(data) {
        this.$apollo.mutate({
          mutation: SetLoadingIndicator,
          variables: {
            isIndicatorLoading: false,
          },
        });
        if (data.getMicrosoftCRMMarketingLists.length === 0) {
          return [{ value: null, text: 'No results)' }];
        }
        const marketingList = data.getMicrosoftCRMMarketingLists.map((item) => ({
          value: item.marketing_list_id,
          text: item.marketing_list_name,
        }));
        marketingList.sort((a, b) => a.text.localeCompare(b.text));

        // Auto-select best matching list for reimport
        if (this.isReimport && this.personalizationSetName && !this.marketingListIdProp) {
          const bestMatch = this.findBestMatch(this.personalizationSetName, marketingList);
          if (bestMatch) {
            this.marketingList = bestMatch.value;
            this.autoSelectedList = bestMatch.text;
          }
        }

        return [
          { value: null, text: 'Select a marketing list' },
          ...marketingList,
        ];
      },
    },
  },
  async created() {
    if (this.marketingListIdProp) {
      this.marketingList = this.marketingListIdProp;
    }
    await this.$apollo.mutate({
      mutation: SetLoadingIndicator,
      variables: {
        isIndicatorLoading: true,
      },
    });
  },
  methods: {
    calculateSimilarity(stringA, stringB) {
      const strA = stringA.toLowerCase().trim();
      const strB = stringB.toLowerCase().trim();

      // Exact match
      if (strA === strB) {
        return 1.0;
      }

      // Check if one string contains the other
      if (strA.includes(strB) || strB.includes(strA)) {
        const longerLength = Math.max(strA.length, strB.length);
        const shorterLength = Math.min(strA.length, strB.length);
        return shorterLength / longerLength;
      }

      // Levenshtein distance calculation
      const matrix = [];
      const lengthA = strA.length;
      const lengthB = strB.length;

      if (lengthA === 0) return lengthB === 0 ? 1 : 0;
      if (lengthB === 0) return 0;

      // Initialize matrix
      for (let i = 0; i <= lengthB; i++) {
        matrix[i] = [i];
      }
      for (let j = 0; j <= lengthA; j++) {
        matrix[0][j] = j;
      }

      // Fill matrix
      for (let i = 1; i <= lengthB; i++) {
        for (let j = 1; j <= lengthA; j++) {
          if (strB.charAt(i - 1) === strA.charAt(j - 1)) {
            matrix[i][j] = matrix[i - 1][j - 1];
          } else {
            matrix[i][j] = Math.min(
              matrix[i - 1][j - 1] + 1, // substitution
              matrix[i][j - 1] + 1, // insertion
              matrix[i - 1][j] + 1, // deletion
            );
          }
        }
      }

      const distance = matrix[lengthB][lengthA];
      const maxLength = Math.max(lengthA, lengthB);
      return 1 - (distance / maxLength);
    },
    findBestMatch(searchString, marketingLists) {
      if (!searchString || !marketingLists || marketingLists.length === 0) {
        return null;
      }

      let bestMatch = null;
      let highestSimilarity = 0;
      const minimumSimilarityThreshold = 0.4;

      marketingLists.forEach((listItem) => {
        const similarity = this.calculateSimilarity(searchString, listItem.text);
        if (similarity > highestSimilarity && similarity >= minimumSimilarityThreshold) {
          highestSimilarity = similarity;
          bestMatch = listItem;
        }
      });

      return bestMatch;
    },
    async next() {
      await this.$apollo.mutate({
        mutation: SetLoadingIndicator,
        variables: {
          isIndicatorLoading: true,
        },
      });
      this.$emit('next-step', this.marketingList, this.marketingListsOptions);
    },
  },
};
</script>

<style scoped lang="scss">
.modal-body h4 {
  font-size: 15px;
  font-weight: bold;
  color: #44484C;
}

ul {
  padding-left: 0;
  list-style: none;

  li {
    color: #44484C;

    ::v-deep  svg {
      color: #B9B9B9;

      &.active {
        color: #53DC86;
      }
    }

    &.past-step {
      cursor: pointer;
    }
  }
}

div.step-1 {
  min-height: 120px;
}

.grey-line {
  height: 30px;
  border-left: 1px solid #B9B9B9;
  margin-left: 6px;
}

.show-url-icon svg {
  font-size: 60px;
}

.show-url {
  font-size: 1.1rem;
}

.loading-spinner {
  min-height: 400px;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.background-spinner {
  position: absolute;
  inset: 0px;
  opacity: 0.85;
  backdrop-filter: blur(2px);
  width: 100%;
  height: 100%;
  background-color: #f8f9fa;
}
.disabled {
  cursor: not-allowed;
}
</style>
